import { useContext, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Button } from 'ui-components/components/button';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';

import AmazonOrderTooltip from './amazon-order-tooltip';
import BulkUpdateBar from './bulk-update';
import { MARK_AS_SHIPPED_SHIP_FROM_ID } from './constants';
import { MarkAsShippedContext } from './context';
import ErrorMessage from './error-message';
import Grid from './grid';
import style from './style.module.less';
import { ComponentStates } from '../../enums/component-states';
import { useShipFroms } from '../../hooks/ship-froms/use-ship-froms';
import { useOnComponentMount } from '../../hooks/use-on-component-mount';
import Global from '../../store/global-selectors';
import { markShipped } from '../../store/mark-as-shipped/mark-as-shipped.actions';

const validateFulfillmentPlan = (fulfillmentPlan) => {
  return fulfillmentPlan.carrierId;
};

const MarkAsShipped = ({ closeModal }) => {
  const { fulfillmentPlans, fulfillmentPlanIds, salesOrders, setHasError, hasError, shouldForceShipFrom, bulkUpdateFulfillmentPlans } =
    useContext(MarkAsShippedContext);

  const dispatch = useDispatch();
  const entities = useSelector((state) => Global.selectEntitiesFulfillmentPlansByIds(state, { fulfillmentPlanIds }));

  const areAllFulfillmentPlansExternal = useMemo(() => {
    return entities.every((fp) => fp.isExternal);
  }, [entities]);

  const markTheseShipped = useCallback(() => {
    const apiFulfillmentPlans = fulfillmentPlanIds.map((fulfillmentPlanId) => {
      const fulfillmentPlan = fulfillmentPlans[fulfillmentPlanId];
      return {
        fulfillmentPlanId,
        trackingNumber: fulfillmentPlan.trackingNumber,
        carrierId: fulfillmentPlan.carrierId,
        shouldNotifyMarketplace: fulfillmentPlan.notifyMarketplace,
        shouldNotifyCustomer: fulfillmentPlan.notifyCustomer,
        shipFromId: fulfillmentPlan.shipFromId,
        allocateInventory: fulfillmentPlan.deductInventory,
        shipDate: fulfillmentPlan.shipDate,
      };
    });
    const isValid = apiFulfillmentPlans.every(validateFulfillmentPlan);
    if (!isValid) {
      setHasError(true);
      return;
    }
    dispatch(markShipped({ fulfillmentPlans: apiFulfillmentPlans }));
    closeModal();
  }, [fulfillmentPlanIds, dispatch, closeModal, fulfillmentPlans, setHasError]);

  const selectionIncludesAmazonOrder = salesOrders.some((order) => order?.store?.marketplaceCode?.toLowerCase()?.includes('amazon'));
  const selectionIncludesRestrictedOrder = entities.some(
    ({ restrictions }) => restrictions?.labelConfiguration?.componentState === ComponentStates.Hidden,
  );

  const shipFroms = useShipFroms();
  const defaultShipFrom = shipFroms.find(({ isDefault }) => isDefault);
  useOnComponentMount(() => {
    if (shouldForceShipFrom && defaultShipFrom) {
      bulkUpdateFulfillmentPlans(MARK_AS_SHIPPED_SHIP_FROM_ID, defaultShipFrom.shipFromId);
    }
  });

  return (
    <>
      <ModalHeader closeModal={closeModal}>
        <div className={style['modal-header']}>
          <FormattedMessage defaultMessage='Mark as Shipped' description='markShippedModal.header' id='lHBwOd' />
          {selectionIncludesAmazonOrder ? <AmazonOrderTooltip /> : null}
          {hasError ? <ErrorMessage /> : null}
        </div>
      </ModalHeader>
      <ModalBody className={style['modal-body']}>
        <div>
          <div className={style['content-container']}>
            <div>
              <FormattedMessage
                defaultMessage="Orders marked as shipped move to <orders>Orders '>' Shipped</orders>. A fulfillment is also created in <shipments>Shipments '>' Recent</shipments>."
                description='marked as shipped header'
                id='LPhFVx'
                values={{
                  orders: (chunks) => <b>{chunks}</b>,
                  shipments: (chunks) => <b>{chunks}</b>,
                }}
              />
            </div>
            <BulkUpdateBar shouldForceShipFrom={shouldForceShipFrom} />
          </div>
          {selectionIncludesRestrictedOrder ? (
            <BannerMessageV2
              className={style['restricted-orders-info']}
              content={
                <FormattedMessage
                  defaultMessage='Selling Channels that provide labels are not eligible for notifications or changes to shipping configurations.'
                  description='marked as shipped modal restricted orders info banner text'
                  id='JlQM6B'
                />
              }
              status={BannerStatus.Info}
            />
          ) : null}
          <Grid />
        </div>
      </ModalBody>
      <ModalFooter className={style['modal-footer']}>
        <Button onClick={closeModal} theme='tertiary'>
          <FormattedMessage defaultMessage='Cancel' description='markAsShipped.cancel' id='R9Y8z6' />
        </Button>
        <Button
          className={style['mark-shipped-button']}
          disabled={areAllFulfillmentPlansExternal}
          onClick={markTheseShipped}
          theme='primary'
        >
          <FormattedMessage defaultMessage='Mark as Shipped' description='markAsShipped.markAsShipped' id='p7Ol21' />
        </Button>
      </ModalFooter>
    </>
  );
};

MarkAsShipped.propTypes = {
  closeModal: PropTypes.func.isRequired,
};

export default MarkAsShipped;
